import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  inject,
  Input,
  Output,
  ViewChild,
} from '@angular/core';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { faFileCsv } from '@fortawesome/pro-regular-svg-icons';
import {
  faCloudArrowUp,
  faDiamondExclamation,
  faTrash,
} from '@fortawesome/pro-solid-svg-icons';
import { NgxCsvParser, NgxCSVParserError } from 'ngx-csv-parser';
import { PrimeTemplate } from 'primeng/api';
import { TableModule } from 'primeng/table';

@Component({
  selector: 'lib-upload-csv',
  imports: [CommonModule, FaIconComponent, PrimeTemplate, TableModule],
  templateUrl: './upload-csv.component.html',
  styleUrl: './upload-csv.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UploadCsvComponent {
  @ViewChild('fileInput') fileInput: ElementRef;
  @Input() columns: string[];
  @Input() header: boolean;
  @Output() csvRows = new EventEmitter<any[]>();
  csvUploadError: string = '';
  uploadedCsvFile: File[] = [];
  isFileDragging: boolean;
  protected readonly faDiamondExclamation = faDiamondExclamation;
  protected readonly faTrash = faTrash;
  protected readonly faCloudArrowUp = faCloudArrowUp;
  protected readonly faFileCSV = faFileCsv;
  private ngxCsvParser = inject(NgxCsvParser);

  @HostListener('dragover', ['$event']) onDragOver(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.isFileDragging = true;
  }

  @HostListener('dragleave', ['$event']) onDragLeave(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.isFileDragging = false;
  }

  @HostListener('drop', ['$event']) onDrop(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.isFileDragging = false;
    if (event.dataTransfer && event.dataTransfer.files) {
      const file = event.dataTransfer.files[0];
      this.processSelectedFile(file);
    }
  }

  /**
   * Opens the file input dialog for selecting a CSV file.
   */
  openFileInput(): void {
    this.fileInput.nativeElement.click();
  }

  /**
   * Handles the event when a file is selected through the file input dialog.
   * @param event The event object containing the selected file.
   */
  handleFileSelection(event: any): void {
    const file = event.target.files[0];
    if (file) {
      // Handle the file selection
      this.processSelectedFile(file);
    }
  }

  /**
   * Processes the selected file by parsing its content.
   * @param file The selected file to be processed.
   */
  processSelectedFile(file: File): void {
    this.parseFile(file);

    const fileInput = document.getElementById('fileInput') as HTMLInputElement;
    if (fileInput) {
      fileInput.value = '';
    }
  }

  /**
   * Triggers the download of a CSV template to upload data
   */
  downloadTemplate(): void {
    let csvContent = 'data:text/csv;charset=utf-8,'; // First header row
    if (this.header) {
      csvContent += this.columns.join(',') + '\n';
    }

    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute('download', 'csv-template.csv');
    document.body.appendChild(link); // Required for FF

    link.click();
    document.body.removeChild(link); // Cleanup the link after download
  }

  /**
   * Parses the CSV file to extract keywords and
   * store them in the appropriate data structure.
   * @param file The CSV file to be parsed.
   */
  parseFile(file: File): void {
    this.ngxCsvParser
      .parse(file, { header: this.header, delimiter: ',' })
      .subscribe({
        next: (result: any): void => {
          this.uploadedCsvFile[0] = file;
          if (!result.length) {
            this.csvUploadError = 'The CSV file appears to be empty.';
            return;
          }

          // Check if the first row has only one column
          if (Object.keys(result[0]).length !== this.columns.length) {
            this.csvUploadError = `The CSV file should have ${this.columns.length} column(s).`;
            return;
          }

          this.csvUploadError = '';
          if (!this.header) {
            result = result.map((row: any) => {
              return this.columns.reduce((acc, key, index) => {
                acc[key] = row[index];
                return acc;
              }, {});
            });
          }
          this.csvRows.emit(result);
        },
        error: (error: NgxCSVParserError): void => {
          this.csvUploadError = 'An error occurred while parsing the CSV file.';
          console.error('Error', error);
        },
      });
  }
}
